import Papa, { ParseResult, ParseConfig } from 'papaparse';
import { CSVConfig, ValidationSchema } from '../types/csv';

/**
 * Service générique pour l'importation de fichiers CSV
 * Ce service fournit des fonctionnalités de base pour parser, mapper et valider des données CSV
 */
export class GenericCSVImportService<T> {
  /**
   * Parse le fichier CSV avec la configuration spécifiée
   * @param file Fichier CSV à parser
   * @param config Configuration CSV (optionnelle)
   * @returns Données parsées, en-têtes et aperçu
   */
  async parseCSV(
    file: File, 
    config: CSVConfig | null
  ): Promise<{
    data: Record<string, any>[];
    headers: string[];
    preview: Record<string, any>[];
  }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const fileContent = event.target?.result as string;
          
          // Si aucune configuration n'est fournie, détecter automatiquement le format
          const format = config ? 
            { delimiter: config.delimiter, headers: [] } : 
            this.detectCSVFormat(fileContent);
          
          // Parser le CSV avec Papa Parse
          const parseConfig: ParseConfig = {
            delimiter: format.delimiter,
            header: true,
            skipEmptyLines: true,
            transformHeader: (header: string) => header.trim()
          };
          
          // Nous ne pouvons pas utiliser skipFirstNRows directement
          // Nous allons gérer les lignes à ignorer manuellement après le parsing
          
          const result: ParseResult<Record<string, any>> = Papa.parse(fileContent, parseConfig);
          
          if (result.errors && result.errors.length > 0) {
            reject(new Error(`Erreur de parsing CSV: ${result.errors[0].message}`));
            return;
          }
          
          const headers = result.meta.fields || [];
          
          resolve({
            data: result.data,
            headers,
            preview: result.data.slice(0, 5),
          });
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Erreur de lecture du fichier'));
      };
      
      reader.readAsText(file);
    });
  }
  
  /**
   * Détecte automatiquement le format CSV (délimiteur)
   * @param fileContent Contenu du fichier CSV
   * @returns Délimiteur détecté et en-têtes
   */
  detectCSVFormat(fileContent: string): {
    delimiter: string;
    headers: string[];
  } {
    // Essayer différents délimiteurs
    const delimiters = [';', ',', '\t', '|'];
    let bestResult: { delimiter: string; headers: string[] } | null = null;
    let maxFields = 0;
    
    for (const delimiter of delimiters) {
      const result = Papa.parse<string[]>(fileContent.split('\n')[0], {
        delimiter,
        preview: 1,
        header: false
      });
      
      const fields = result.data[0]?.length || 0;
      if (fields > maxFields) {
        maxFields = fields;
        bestResult = {
          delimiter,
          headers: result.data[0] || [],
        };
      }
    }
    
    return bestResult || { delimiter: ',', headers: [] };
  }
  
  /**
   * Applique un mapping générique aux données brutes
   * @param rawData Données brutes du CSV
   * @param mapping Mapping des colonnes
   * @returns Données mappées selon le type T
   */
  applyMapping<K>(
    rawData: Record<string, any>[],
    mapping: Record<keyof K, string>
  ): Partial<K>[] {
    return rawData.map(row => {
      const result = {} as Partial<K>;
      
      // Utiliser Object.keys avec une assertion de type pour éviter les erreurs
      (Object.keys(mapping) as Array<keyof K>).forEach(key => {
        const column = mapping[key];
        if (column && row[column] !== undefined) {
          result[key] = row[column];
        }
      });
      
      return result;
    });
  }
  
  /**
   * Valide les données selon un schéma
   * @param data Données à valider
   * @param validationSchema Schéma de validation
   * @returns Données valides et invalides avec raisons
   */
  validateData<K>(
    data: Partial<K>[], 
    validationSchema: ValidationSchema
  ): {
    valid: Partial<K>[];
    invalid: Array<{data: Partial<K>, reason: string, line: number}>;
  } {
    const valid: Partial<K>[] = [];
    const invalid: Array<{data: Partial<K>, reason: string, line: number}> = [];
    
    data.forEach((item, index) => {
      let isValid = true;
      let reason = '';
      
      // Vérifier les champs requis
      for (const field of validationSchema.required) {
        if (item[field as keyof K] === undefined || item[field as keyof K] === null || item[field as keyof K] === '') {
          isValid = false;
          reason = `Champ requis manquant: ${field}`;
          break;
        }
      }
      
      // Vérifier les types
      if (isValid && validationSchema.types) {
        for (const [field, type] of Object.entries(validationSchema.types)) {
          const value = item[field as keyof K];
          
          if (value !== undefined && value !== null) {
            if (type === 'number' && isNaN(Number(value))) {
              isValid = false;
              reason = `Le champ ${field} doit être un nombre`;
              break;
            } else if (type === 'date' && isNaN(Date.parse(String(value)))) {
              isValid = false;
              reason = `Le champ ${field} doit être une date valide`;
              break;
            }
          }
        }
      }
      
      // Vérifier les patterns
      if (isValid && validationSchema.patterns) {
        for (const [field, pattern] of Object.entries(validationSchema.patterns)) {
          const value = item[field as keyof K];
          
          if (value !== undefined && value !== null && !pattern.test(String(value))) {
            isValid = false;
            reason = `Le champ ${field} ne correspond pas au format attendu`;
            break;
          }
        }
      }
      
      // Vérifier les validations personnalisées
      if (isValid && validationSchema.custom) {
        for (const [field, validator] of Object.entries(validationSchema.custom)) {
          const value = item[field as keyof K];
          
          if (value !== undefined && value !== null && !validator(value)) {
            isValid = false;
            reason = `Le champ ${field} n'est pas valide`;
            break;
          }
        }
      }
      
      if (isValid) {
        valid.push(item);
      } else {
        invalid.push({ data: item, reason, line: index + 2 }); // +2 car ligne 1 = en-têtes, et index commence à 0
      }
    });
    
    return { valid, invalid };
  }
  
  /**
   * Suggère un mapping automatique basé sur les en-têtes
   * @param headers En-têtes du CSV
   * @param fields Champs attendus
   * @returns Mapping suggéré
   */
  suggestMapping<K>(headers: string[], fields: Array<keyof K>): Partial<Record<keyof K, string>> {
    const mapping: Partial<Record<keyof K, string>> = {};
    const lowerHeaders = headers.map(h => h.toLowerCase());
    
    // Fonction pour trouver un en-tête correspondant à un pattern
    const findHeaderForPattern = (patterns: string[]): string | undefined => {
      for (const pattern of patterns) {
        const index = lowerHeaders.findIndex(h => h.includes(pattern));
        if (index >= 0) {
          return headers[index];
        }
      }
      return undefined;
    };
    
    // Pour chaque champ, chercher un en-tête correspondant
    for (const field of fields) {
      const fieldStr = String(field);
      
      // Définir les patterns pour chaque type de champ
      let patterns: string[] = [];
      
      switch (fieldStr) {
        case 'date':
          patterns = ['date', 'jour', 'jour_transaction'];
          break;
        case 'heure':
          patterns = ['heure', 'time', 'horodatage'];
          break;
        case 'matricule':
          patterns = ['matricule', 'immatriculation', 'plaque', 'vehicule'];
          break;
        case 'kilometresAvant':
          patterns = ['kilometres avant', 'km avant', 'kilometresavant', 'kmavant'];
          break;
        case 'kilometres':
          patterns = ['kilometres', 'km', 'kilométrage', 'odometre'];
          break;
        case 'typeProduit':
          patterns = ['type produit', 'type', 'produit type', 'type operation', 'typeoperation'];
          break;
        case 'produit':
          patterns = ['produit', 'article', 'carburant'];
          break;
        case 'quantite':
          patterns = ['quantite', 'qte', 'volume', 'litres'];
          break;
        case 'montant':
          patterns = ['montant', 'prix', 'cout', 'total'];
          break;
        case 'lieu':
          patterns = ['lieu', 'station', 'site', 'adresse'];
          break;
        case 'numFacture':
          patterns = ['facture', 'num facture', 'numero facture', 'reference'];
          break;
      }
      
      const matchedHeader = findHeaderForPattern(patterns);
      if (matchedHeader) {
        mapping[field] = matchedHeader;
      }
    }
    
    return mapping;
  }
}